import { Button, Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { useSearchParams } from 'react-router-dom'

import { FilterField } from '../common/list-page.js'
import { catalogScope } from './catalog-navigation.js'
import type { ContentDashboard } from './types.js'

export function ContentSelect({ label, value, onChange, options, emptyLabel = 'All', disabled = false }: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  emptyLabel?: string
  disabled?: boolean
}): JSX.Element {
  const id = `content-${label.toLowerCase().replaceAll(' ', '-')}`
  return <FilterField label={label} htmlFor={id} active={value !== ''}>
    <div className="relative flex w-full items-center">
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} size="sm" className={value && !disabled ? "pr-14 [&>[data-slot=select-icon]]:absolute [&>[data-slot=select-icon]]:right-3" : undefined}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="">{emptyLabel}</SelectItem>
        {value && !options.some(option => option.value === value) ? <SelectItem value={value}>Unavailable selection</SelectItem> : null}
        {options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {value && !disabled ? <Button type="button" variant="ghost" size="icon" aria-label={`Clear ${label.toLowerCase()} filter`} title={`Clear ${label.toLowerCase()} filter`} onClick={() => onChange('')} className="absolute right-7 size-6 text-text-secondary hover:text-text-primary"><Icon name="clear" size={14} /></Button> : null}
    </div>
  </FilterField>
}

export function useContentScope(dashboard: ContentDashboard) {
  const [params, setParams] = useSearchParams()
  const scope = catalogScope(dashboard, params)
  const programId = scope.programId
  const selectedLevel = scope.valid ? scope.level : undefined
  const levels = dashboard.levels.filter(row => !programId || row.programId === programId)
  function update(values: Record<string, string>): void {
    const next = new URLSearchParams(params)
    next.delete('page')
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setParams(next, { replace: true })
  }
  return { programId, selectedLevel, requestedLevelId: scope.levelId, levels, update }
}

export function ContentScopeFilters({ dashboard, required = false }: { dashboard: ContentDashboard; required?: boolean }): JSX.Element {
  const { programId, requestedLevelId, levels, update } = useContentScope(dashboard)
  const programs = dashboard.programs.map(program => ({ value: program.id, label: program.name.en }))
  return <>
    <ContentSelect label="Program" value={programId} options={programs} emptyLabel={required ? 'Select a Program' : 'All Programs'}
      onChange={program => update({ program, level: '', theme: '', grade: '' })} />
    <ContentSelect label="Level" value={requestedLevelId}
      disabled={required && !programId} emptyLabel={required ? 'Select a level' : 'All levels'}
      options={levels.map(row => ({ value: row.programLevelId, label: programId ? row.level : `${row.programName.en} · ${row.level}` }))}
      onChange={level => update({ level, program: dashboard.levels.find(row => row.programLevelId === level)?.programId ?? programId, theme: '', grade: '' })} />
  </>
}
